import { useState } from 'react';
import { Button, Checkbox, Switch, Textarea } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useModifierReglagesBoutique,
  useReglagesBoutique,
  type ReglagesBoutique,
} from '../../api/boutique.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useLangue } from '../../composants/useLangue.js';
import { useShellContext } from '../shell/context.js';
import { ErreurBoutique } from './commun.js';
import styles from './BoutiquePage.module.css';
const POINTS_A_COUVRIR = ['prix', 'frais', 'livraison', 'retours'] as const;
/** Les deux étapes pour ouvrir la boutique, avec celle qui reste à faire. */
function Parcours({ reglages }: { reglages: ReglagesBoutique }) {
  const { t } = useTranslation('regie');
  const langue = useLangue();
  const etapes = [
    { cle: 'validation', fait: reglages.conditionsValidees },
    { cle: 'ouverture', fait: reglages.ouverte },
  ] as const;
  const courante = etapes.findIndex((e) => !e.fait);
  return (
    <section aria-labelledby="boutique-parcours">
      <h2 id="boutique-parcours" className={styles.titreSection}>
        {t('boutique.parcours.titre')}
      </h2>
      <ol className={styles.parcours}>
        {etapes.map((e, i) => (
          <li
            key={e.cle}
            data-statut={e.fait ? 'fait' : i === courante ? 'courante' : 'attente'}
            aria-current={i === courante ? 'step' : undefined}
          >
            <span className={styles.parcoursPuce} aria-hidden>
              {e.fait ? <Check size={14} strokeWidth={2} /> : i + 1}
            </span>
            <span>
              <strong>{t(`boutique.parcours.${e.cle}`)}</strong>
              <span className={styles.secondaire}>
                {e.fait
                  ? e.cle === 'validation' && reglages.conditionsValideesLe
                    ? t('boutique.parcours.validee', {
                        date: formatDate(new Date(reglages.conditionsValideesLe), langue),
                      })
                    : t('boutique.parcours.fait')
                  : t(i === courante ? 'boutique.parcours.enCours' : 'boutique.parcours.attente')}
              </span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
/** Guide : les points que le texte des conditions doit traiter (frais, livraison, retours…). */
function PointsACouvrir() {
  const { t } = useTranslation('regie');
  return (
    <section aria-labelledby="boutique-a-couvrir">
      <h2 id="boutique-a-couvrir" className={styles.titreSection}>
        {t('boutique.aCouvrir.titre')}
      </h2>
      <p className={styles.secondaire}>{t('boutique.aCouvrir.aide')}</p>
      <ul className={styles.points}>
        {POINTS_A_COUVRIR.map((p) => (
          <li key={p}>
            <strong>{t(`boutique.aCouvrir.${p}.titre`)}</strong>
            <span className={styles.secondaire}>{t(`boutique.aCouvrir.${p}.texte`)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
function FormulaireConditions({
  reglages,
  peutEcrire,
  galerie,
}: {
  reglages: ReglagesBoutique;
  peutEcrire: boolean;
  galerie: string;
}) {
  const { t } = useTranslation('regie');
  const mutation = useModifierReglagesBoutique(galerie);
  const [fr, setFr] = useState(reglages.conditionsVente.fr),
    [en, setEn] = useState(reglages.conditionsVente.en ?? '');
  const [validation, setValidation] = useState(false);
  const [enregistre, setEnregistre] = useState(false);
  const modifie = fr !== reglages.conditionsVente.fr || en !== (reglages.conditionsVente.en ?? '');
  const bloque = !peutEcrire || mutation.isPending;
  return (
    <div className={styles.conditions}>
      <Parcours reglages={reglages} />
      {!reglages.conditionsValidees && (
        <aside className={styles.avertissement}>
          <strong>{t('boutique.modele')}</strong>
          <p>{t('boutique.modeleAide')}</p>
        </aside>
      )}
      <div className={styles.ouverture}>
        <div>
          <h2>{t('boutique.ouverture')}</h2>
          <p>{t('boutique.ouvertureAide')}</p>
        </div>
        <Switch
          id="boutique-ouverte"
          label={t('boutique.boutiqueOuverte')}
          checked={reglages.ouverte}
          disabled={bloque || !reglages.conditionsValidees || modifie}
          onCheckedChange={(ouverte) => mutation.mutate({ ouverte })}
        />
      </div>
      {mutation.isError && <ErreurBoutique erreur={mutation.error} />}
      <PointsACouvrir />
      <h2 className={styles.titreSection}>{t('boutique.redigerTitre')}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate(
            { conditionsVente: { fr, en } },
            {
              onSuccess: () => {
                setValidation(false);
                setEnregistre(true);
              },
            },
          );
        }}
      >
        <div className={styles.textes}>
          <label>
            <span>{t('boutique.conditionsFr')}</span>
            <Textarea
              rows={12}
              maxLength={30000}
              required
              value={fr}
              disabled={bloque}
              onChange={(e) => {
                setFr(e.target.value);
                setEnregistre(false);
              }}
            />
          </label>
          <label>
            <span>{t('boutique.conditionsEn')}</span>
            <Textarea
              rows={12}
              maxLength={30000}
              value={en}
              disabled={bloque}
              onChange={(e) => {
                setEn(e.target.value);
                setEnregistre(false);
              }}
            />
          </label>
        </div>
        <div className={styles.actions}>
          <Button
            type="submit"
            disabled={bloque || !modifie || !fr.trim()}
            loading={mutation.isPending}
          >
            {t('boutique.enregistrer')}
          </Button>
          {enregistre && !modifie && <span role="status">{t('boutique.enregistre')}</span>}
        </div>
      </form>
      <section className={styles.validation}>
        <h2>{t('boutique.validation')}</h2>
        <p className={styles.secondaire}>{t('boutique.validationAide')}</p>
        {reglages.conditionsValidees ? (
          <p role="status">{t('boutique.conditionsValidees')}</p>
        ) : (
          <>
            <Checkbox
              id="conditions-validees"
              label={t('boutique.validationCase')}
              checked={validation}
              disabled={bloque || modifie}
              onCheckedChange={(v) => setValidation(v === true)}
            />
            <Button
              variant="secondary"
              disabled={bloque || modifie || !validation}
              onClick={() => mutation.mutate({ conditionsValidees: true })}
            >
              {t('boutique.valider')}
            </Button>
          </>
        )}
      </section>
    </div>
  );
}
export function ConditionsTab() {
  const { t } = useTranslation('commun');
  const { galerieSlug, galerie } = useShellContext();
  const reglages = useReglagesBoutique(galerieSlug);
  if (reglages.isLoading) return <p role="status">{t('chargement')}</p>;
  if (reglages.isError)
    return <ErreurBoutique erreur={reglages.error} reessayer={() => void reglages.refetch()} />;
  return reglages.data ? (
    <FormulaireConditions
      key={galerieSlug}
      reglages={reglages.data}
      galerie={galerieSlug}
      peutEcrire={galerieALaction(galerie, 'shop:write')}
    />
  ) : null;
}
